import {useState, useMemo, Fragment} from "react";
import {useTranslation} from "react-i18next";
import "../styles/CharacterSelector.scss";
import {SLIDER_CONFIG} from "../../../assets/constants/sliderConfig";
import {CharacterInfo} from "../assets/constants/characterInfo";
import {useCharacter} from "..";
import {Artist} from "src/utils/artist.utils";
import {getCurrentLng, SupportedLng} from "src/utils/language.utils";
import {SessionTimer} from "src/services/session";

type CharacterSelectorProps = {
  artist: Artist;
  onSelect: () => void;
  onExpire: () => void;
};

const CharacterSelector = ({
  artist,
  onSelect,
  onExpire,
}: CharacterSelectorProps) => {
  const {t} = useTranslation("common");

  const {
    getCharactersByArtist,
    changeSelectedCharacter,
    selectedCharacter,
    saveCharacter,
  } = useCharacter({
    artist,
  });

  const characters = getCharactersByArtist(artist);
  const handleSelect = () => {
    saveCharacter();
    onSelect();
  };

  return (
    <div className="character-selector-container">
      <div className="character-selector-content">
        <div className="character-selector-instruction">
          {t("selectCharacter.instruction")}
        </div>

        <div className="character-selector-layout-wrapper">
          <CharacterCarousel
            characters={characters}
            selectedCharacter={selectedCharacter}
            artist={artist}
            onCharacterChange={changeSelectedCharacter}
          />
          <div className="character-selector-session-timer-wrapper">
            <SessionTimer onExpire={onExpire} />
          </div>

          <button
            type="button"
            className={`character-selector-select-button ${artist}`}
            onClick={handleSelect}
          >
            {t("selectCharacter.selectButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterSelector;

type CharacterCarouselProps = {
  characters: CharacterInfo[];
  selectedCharacter: CharacterInfo | null;
  artist: Artist;
  onCharacterChange: (character: CharacterInfo) => void;
};

const CharacterCarousel = ({
  characters,
  selectedCharacter,
  artist,
  onCharacterChange,
}: CharacterCarouselProps) => {
  const currentLanguage: SupportedLng = getCurrentLng();

  const currentIndex = useMemo(() => {
    if (!selectedCharacter) {
      return 0;
    }
    const index = characters.findIndex(
      (char) => char.id === selectedCharacter.id,
    );
    return index !== -1 ? index : 0;
  }, [selectedCharacter, characters]);

  const [dragStart, setDragStart] = useState<number>(0);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleThumbnailClick = (character: CharacterInfo) => {
    onCharacterChange(character);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      onCharacterChange(characters[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < characters.length - 1) {
      onCharacterChange(characters[currentIndex + 1]);
    }
  };

  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setDragStart(clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleDragMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - dragStart;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    if (Math.abs(dragOffset) > SLIDER_CONFIG.DRAG_THRESHOLD) {
      if (dragOffset > 0 && currentIndex > 0) {
        onCharacterChange(characters[currentIndex - 1]);
      }
      if (dragOffset < 0 && currentIndex < characters.length - 1) {
        onCharacterChange(characters[currentIndex + 1]);
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  if (characters.length === 0) return null;

  const slidePercent = 100 / characters.length;
  const translateXPercent = currentIndex * slidePercent;

  return (
    <div className="character-selector-carousel-wrapper">
      <div className="character-selector-thumbnails">
        {characters.map((character) => (
          <button
            key={`${artist}-${character.id}-${character.identifier}`}
            type="button"
            className={`character-selector-thumbnail ${artist} ${
              selectedCharacter?.id === character.id ? "active" : ""
            } ${character.identifier}`}
            onClick={() => handleThumbnailClick(character)}
            aria-label={character.information[currentLanguage].name}
            aria-pressed={selectedCharacter?.id === character.id}
          >
            <img
              src={character.thumbnail}
              alt=""
              className="character-selector-thumbnail-image"
            />
          </button>
        ))}
      </div>

      <div
        className="character-selector-card-wrap"
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
      >
        <button
          type="button"
          className={`character-selector-arrow character-selector-arrow-prev ${artist}`}
          onClick={handlePrev}
          disabled={currentIndex <= 0}
          aria-label="Previous character"
        >
          ‹
        </button>

        <div className="character-selector-card-viewport">
          <div
            className="character-selector-card-slider"
            style={{
              width: `${characters.length * 100}%`,
              transform: `translateX(calc(-${translateXPercent}% + ${dragOffset}px))`,
              transition: isDragging
                ? "none"
                : `transform ${SLIDER_CONFIG.TRANSITION_DURATION}ms ease-in-out`,
            }}
          >
            {characters.map((character) => {
              return (
                <div
                  key={`${artist}-${character.id}-${character.identifier}`}
                  className={`character-selector-card ${character.identifier}`}
                  style={{
                    width: `${slidePercent}%`,
                    flexShrink: 0,
                  }}
                >
                  <div className="character-selector-card-greeting">
                    {character.information[currentLanguage].greeting}
                  </div>
                  <div className="character-selector-card-image-wrap">
                    <img
                      src={character.image}
                      alt={""}
                      className="character-selector-card-image"
                      draggable={false}
                    />
                  </div>
                  <div className="character-selector-card-character-info">
                    <div className="character-selector-card-character-name">
                      {character.information[currentLanguage].name}
                    </div>
                    <div className="character-selector-card-tags">
                      {character.information[currentLanguage].personality.map(
                        (personality, index) => (
                          <Fragment
                            key={`${artist}-${character.id}-${character.identifier}-${personality}`}
                          >
                            {`#${personality}${index < character.information[currentLanguage].personality.length - 1 ? ", " : ""}`}
                          </Fragment>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className={`character-selector-arrow character-selector-arrow-next ${artist}`}
          onClick={handleNext}
          disabled={currentIndex >= characters.length - 1}
          aria-label="Next character"
        >
          ›
        </button>
      </div>
    </div>
  );
};
